import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

export default function ViabilityScore({ score }) {
  const getColor = () => {
    if (score >= 85) return '#a8a8a8'
    if (score >= 70) return '#7a7a7a'
    if (score >= 50) return '#4a4a4a'
    return '#2d2d2d'
  }

  const getLevel = () => {
    if (score >= 85) return '🔥 EXCELENTE'
    if (score >= 70) return '✅ VIABLE'
    if (score >= 50) return '⚠️ RIESGOSO'
    return '❌ NO VIABLE'
  }

  const data = [
    { name: 'Score', value: score }
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Score principal */}
      <div className="gradient-glow p-6 rounded-xl card-hover">
        <p className="text-slate-400 text-sm mb-4">Puntuación de Viabilidad</p>
        <div className="text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="rgba(122, 122, 122, 0.1)"
                strokeWidth="4"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke={getColor()}
                strokeWidth="4"
                strokeDasharray={`${(score / 100) * 282.7} 282.7`}
                strokeLinecap="round"
              />
              <text
                x="50"
                y="55"
                textAnchor="middle"
                className="text-2xl font-bold"
                fill={getColor()}
              >
                {score}
              </text>
            </svg>
          </div>
          <p className="text-xl font-bold text-slate-100 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{getLevel()}</p>
          <p className="text-xs text-slate-500">De 0-100</p>
        </div>
      </div>

      {/* Recomendación */}
      <div className="gradient-glow p-6 rounded-xl card-hover">
        <p className="text-slate-400 text-sm mb-4">Recomendación</p>
        <div className="space-y-3">
          {score >= 85 ? (
            <>
              <div className="flex items-start gap-2">
                <span className="text-[#94a3b8]er text-lg">✓</span>
                <span className="text-sm text-slate-300">Señal de mercado muy fuerte</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#94a3b8]er text-lg">✓</span>
                <span className="text-sm text-slate-300">Alta demanda detectada en múltiples fuentes</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#94a3b8]er text-lg">✓</span>
                <span className="text-sm text-slate-300">Considerar lanzamiento con presupuesto completo</span>
              </div>
            </>
          ) : score >= 70 ? (
            <>
              <div className="flex items-start gap-2">
                <span className="text-[#94a3b8] text-lg">→</span>
                <span className="text-sm text-slate-300">Señal favorable — validar con audiencia pequeña</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#94a3b8] text-lg">→</span>
                <span className="text-sm text-slate-300">A/B testing de estrategia recomendado</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#94a3b8] text-lg">→</span>
                <span className="text-sm text-slate-300">Investigación adicional antes de escalar</span>
              </div>
            </>
          ) : score >= 50 ? (
            <>
              <div className="flex items-start gap-2">
                <span className="text-slate-medium text-lg">⚠</span>
                <span className="text-sm text-slate-300">Señal mixta — requiere investigación profunda</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-medium text-lg">⚠</span>
                <span className="text-sm text-slate-300">Validar nicho con campaña piloto pequeña</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-medium text-lg">⚠</span>
                <span className="text-sm text-slate-300">Considerar especialización del producto</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2">
                <span className="text-slate-medium text-lg">✗</span>
                <span className="text-sm text-slate-300">Señal débil — revisar keyword o segmento</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-medium text-lg">✗</span>
                <span className="text-sm text-slate-300">Considerar producto o mercado alternativo</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-medium text-lg">✗</span>
                <span className="text-sm text-slate-300">Datos insuficientes para recomendación positiva</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Interpretación del score */}
      <div className="gradient-glow p-6 rounded-xl card-hover">
        <p className="text-slate-400 text-sm mb-4">Interpretación</p>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-slate-500">Señal de mercado</p>
            <p className="text-lg font-bold text-[#94a3b8]">
              {score >= 85 ? 'Muy fuerte' : score >= 70 ? 'Favorable' : score >= 50 ? 'Mixta' : 'Débil'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Nivel de competencia</p>
            <p className={`text-lg font-bold ${
              score >= 85 ? 'text-[#94a3b8]er' :
              score >= 70 ? 'text-[#94a3b8]' :
              'text-slate-medium'
            }`}>
              {score >= 85 ? 'Moderada' : score >= 70 ? 'Media' : score >= 50 ? 'Alta' : 'Muy alta'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Acción sugerida</p>
            <p className="text-lg font-bold text-slate-medium">
              {score >= 85 ? 'Proceder' : score >= 70 ? 'Validar' : score >= 50 ? 'Investigar' : 'Descartar'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
